import { classNames } from './class-names.js';
import type { FlowRoute, OnboardingProgress } from './flow-route-types.js';
import { isRouteReachable, stepNumberFor } from './gating.js';
import { normalizePath } from './router.js';

type ItemState = 'done' | 'active' | 'blocked' | 'todo';

export interface StepRailProps {
  steps: readonly FlowRoute[];
  visiblePath: string;
  progress: OnboardingProgress;
  onNavigate: (path: string) => void;
}

export function StepRail({ steps, visiblePath, progress, onNavigate }: StepRailProps) {
  const activePath = normalizePath(visiblePath);
  const activeStep = steps.find((step) => step.path === activePath) ?? steps[0];
  const activeProgress = progressTextForMobile(steps, activeStep);

  return (
    <>
      <aside className="hidden lg:block lg:w-80 lg:shrink-0" aria-label="Steps dos primeiros passos">
        <RailIntro />
        <nav aria-label="Ir para uma step">
          <ul className="steps steps-vertical w-full">
            {steps.map((step) => (
              <RailItem
                key={step.path}
                step={step}
                steps={steps}
                state={itemState(steps, step, activePath, progress)}
                onNavigate={onNavigate}
              />
            ))}
          </ul>
        </nav>
      </aside>

      <section className="lg:hidden" aria-label="Steps dos primeiros passos">
        <div className="rounded-box border border-base-300 bg-base-100 p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">
            Teste grátis
          </p>
          <div className="mt-1 flex min-w-0 items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold">{activeStep.title}</h2>
              {activeProgress && <p className="text-sm opacity-70">{activeProgress}</p>}
            </div>
            <span className="badge badge-primary shrink-0">{railMarker(steps, activeStep, 'active')}</span>
          </div>
        </div>

        <nav className="mt-3 overflow-x-auto pb-1" aria-label="Ir para uma step">
          <ul className="flex min-w-max gap-2 pr-4">
            {steps.map((step) => {
              const state = itemState(steps, step, activePath, progress);

              return (
                <li key={step.path}>
                  <button
                    type="button"
                    className={classNames(
                      'btn btn-sm whitespace-nowrap',
                      state === 'active' && 'btn-primary',
                      state !== 'active' && 'btn-ghost bg-base-100',
                      state === 'blocked' && 'opacity-50',
                    )}
                    onClick={() => onNavigate(step.path)}
                    disabled={state === 'blocked'}
                    aria-current={ariaCurrent(state)}
                    aria-disabled={state === 'blocked'}
                  >
                    <span aria-hidden="true">{railMarker(steps, step, state)}</span>
                    {step.title}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </section>
    </>
  );
}

function RailIntro() {
  return (
    <div className="mb-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-primary">
        Teste grátis
      </p>
      <h2 className="mt-1 text-xl font-semibold">Primeiros passos</h2>
      <p className="mt-2 text-sm opacity-70">
        Configure sua loja, conecte o WhatsApp e comece seus 7 dias de teste grátis.
      </p>
    </div>
  );
}

interface RailItemProps {
  step: FlowRoute;
  steps: readonly FlowRoute[];
  state: ItemState;
  onNavigate: (path: string) => void;
}

function RailItem({ step, steps, state, onNavigate }: RailItemProps) {
  return (
    <li
      className={classNames('step text-left', railStepClass(state))}
      data-content={railMarker(steps, step, state)}
      aria-current={ariaCurrent(state)}
    >
      <button
        type="button"
        className={classNames(
          'w-full py-1 text-left text-sm',
          state === 'blocked' && 'cursor-not-allowed opacity-50',
          state === 'active' && 'font-semibold',
        )}
        onClick={() => onNavigate(step.path)}
        disabled={state === 'blocked'}
        aria-disabled={state === 'blocked'}
      >
        <span className="block">{step.title}</span>
      </button>
    </li>
  );
}

function itemState(
  steps: readonly FlowRoute[],
  step: FlowRoute,
  activePath: string,
  progress: OnboardingProgress,
): ItemState {
  if (step.path === activePath) {
    return 'active';
  }

  if (!isRouteReachable(steps, step, progress)) {
    return 'blocked';
  }

  if (isSequentiallyComplete(steps, step, progress)) {
    return 'done';
  }

  return 'todo';
}

function isSequentiallyComplete(
  steps: readonly FlowRoute[],
  step: FlowRoute,
  progress: OnboardingProgress,
): boolean {
  const stepIndex = steps.findIndex((candidate) => candidate.path === step.path);

  if (stepIndex < 0) {
    return false;
  }

  for (const previousStep of steps.slice(0, stepIndex)) {
    if (!previousStep.isComplete(progress)) {
      return false;
    }
  }

  return step.isComplete(progress);
}

function railStepClass(state: ItemState): string | false {
  if (state === 'done') {
    return 'step-primary';
  }

  if (state === 'active') {
    return 'step-primary';
  }

  return false;
}

function railMarker(steps: readonly FlowRoute[], step: FlowRoute, state: ItemState): string {
  if (state === 'done') {
    return '✓';
  }

  if (step.kind !== 'step') {
    return '•';
  }

  return String(stepNumberFor(steps, step));
}

function progressTextForMobile(steps: readonly FlowRoute[], step: FlowRoute): string | undefined {
  if (step.kind === 'step') {
    return `Step ${stepNumberFor(steps, step)} de ${steps.filter((candidate) => candidate.kind === 'step').length}`;
  }

  if (step.kind === 'entry') {
    return 'Boas-vindas';
  }

  return undefined;
}

function ariaCurrent(state: ItemState): 'step' | undefined {
  if (state === 'active') {
    return 'step';
  }

  return undefined;
}
